/**
 * Zod schema for date and dateRange fields.
 * Messages: Required, Invalid format DD/MM/YYYY, Out of range (before X / after X / before X and after Y).
 */

import { z } from 'zod';
import type { FieldSchemaConfig } from '../types';

// DD-MMM-YYYY (day 01-31, month 3-letter abbreviation, year 4 digits)
const DD_MMM_YYYY_REGEX =
  /^(0[1-9]|[12]\d|3[01])-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/i;

const MONTH_ABBR: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

function parseDDMMYYYY(s: string): Date | null {
  const trimmed = s.trim();
  if (!DD_MMM_YYYY_REGEX.test(trimmed)) return null;
  const [dStr, mStr, yStr] = trimmed.split('-');
  const d = Number(dStr);
  const m = MONTH_ABBR[mStr.toLowerCase()];
  const y = Number(yStr);
  if (m === undefined) return null;
  const date = new Date(y, m, d);
  return date.getFullYear() === y && date.getMonth() === m && date.getDate() === d ? date : null;
}

function formatDateForMessage(dateStr: string): string {
  // Assume ISO or DD-MMM-YYYY; return as-is for message
  return dateStr;
}

export function getDateSchema(col: FieldSchemaConfig): z.ZodTypeAny {
  const required = 'required' in col && col.required === true;
  const dateMin = col.dateMin;
  const dateMax = col.dateMax;

  let schema: z.ZodTypeAny = z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v: string | null | undefined) => (v == null ? '' : String(v).trim()));

  if (required) {
    schema = schema.refine((s: string) => s.length >= 1, 'A date is required.') as z.ZodTypeAny;
  }

  schema = schema.refine(
    (s: string) => s.length === 0 || parseDDMMYYYY(s) !== null,
    'Use the format DD-MMM-YYYY.'
  ) as z.ZodTypeAny;

  if (dateMin != null && dateMax != null) {
    const minDate = parseDDMMYYYY(dateMin) ?? new Date(dateMin);
    const maxDate = parseDDMMYYYY(dateMax) ?? new Date(dateMax);
    const minStr = formatDateForMessage(dateMin);
    const maxStr = formatDateForMessage(dateMax);
    schema = schema.refine((s: string) => {
      if (s.length === 0) return true;
      const parsed = parseDDMMYYYY(s);
      return parsed === null || (parsed >= minDate && parsed <= maxDate);
    }, `Date must be before ${maxStr} and after ${minStr}.`) as z.ZodTypeAny;
  } else if (dateMin != null) {
    const minDate = parseDDMMYYYY(dateMin) ?? new Date(dateMin);
    schema = schema.refine(
      (s: string) => {
        if (s.length === 0) return true;
        const parsed = parseDDMMYYYY(s);
        return parsed === null || parsed >= minDate;
      },
      `Date must be after ${formatDateForMessage(dateMin)}.`
    ) as z.ZodTypeAny;
  } else if (dateMax != null) {
    const maxStr = formatDateForMessage(dateMax);
    schema = schema.refine((s: string) => {
      if (s.length === 0) return true;
      const parsed = parseDDMMYYYY(s);
      const maxDate = parseDDMMYYYY(dateMax) ?? new Date(dateMax);
      return parsed === null || parsed <= maxDate;
    }, `Date must be before ${maxStr}.`) as z.ZodTypeAny;
  }

  return schema;
}
